/**
 * LSA Product Card Alpine.js Component
 *
 * Handles:
 * - Dynamic image dimension calculation
 * - Variant/swatch selection with per-variant carousel refresh
 * - Hover carousel behavior (advance/reset)
 * - Touch device detection
 */
const MAX_REFRESH_ATTEMPTS = 6
const REFRESH_RETRY_DELAY_MS = 30
const MAX_IMAGE_WIDTH = 5760
const MIN_WIDTH_DELTA = 40

export default (
  aspectRatio = 1.34,
  firstVariantIndex = 0
) => ({
  aspectRatio,
  activeVariant: firstVariantIndex,
  imageWidth: 0,
  imageHeight: 0,
  imagesReady: false,
  isTouchDevice: false,
  resizeObserver: null,

  init() {
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0
    this.calculateDimensions()

    // Observing the card also covers window resizes and column-count changes,
    // so no separate window resize listener is needed.
    this.resizeObserver = new ResizeObserver(() => this.calculateDimensions())
    this.resizeObserver.observe(this.$el)

    this.$nextTick(() => {
      this.calculateDimensions()
      this.scheduleCarouselRefresh()
    })
  },

  destroy() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
  },

  /**
   * Calculate image dimensions for responsive images.
   * Src is not applied until this succeeds with a non-zero width.
   */
  calculateDimensions() {
    const container = this.$refs.productCardContainer || this.$el
    if (!container) return

    const containerWidth = container.getBoundingClientRect().width
    if (containerWidth <= 0) return

    const devicePixelRatio = window.devicePixelRatio || 1
    const dpr = Math.min(devicePixelRatio, 2)
    const nextWidth = Math.min(
      MAX_IMAGE_WIDTH,
      Math.max(1, Math.round(containerWidth * dpr))
    )
    const nextHeight = Math.max(1, Math.round(nextWidth * this.aspectRatio))

    if (
      this.imagesReady &&
      Math.abs(nextWidth - this.imageWidth) < MIN_WIDTH_DELTA
    ) {
      return
    }

    this.imageWidth = nextWidth
    this.imageHeight = nextHeight
    this.imagesReady = true
  },

  /**
   * Build responsive image URL. Returns undefined until layout is measured
   * so Alpine does not set src (empty src would request the current page).
   */
  getImageUrl(baseUrl) {
    if (!baseUrl || !this.imagesReady) return undefined

    const width = Math.max(1, this.imageWidth)
    const height = Math.max(1, this.imageHeight)

    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("width", String(width))
    url.searchParams.set("height", String(height))

    return url.toString()
  },

  onCardImageLoad(el) {
    el.classList.add("is-loaded")
  },

  /**
   * Retry once at a known-good size, so a rejected resize does not leave
   * a permanently blank tile.
   */
  onCardImageError(el) {
    const fallbackSrc = el.dataset.fallbackSrc

    if (fallbackSrc && el.getAttribute("src") !== fallbackSrc) {
      el.setAttribute("src", fallbackSrc)
      return
    }

    console.warn(
      "Product card image failed to load:",
      el.getAttribute("src")
    )
  },

  selectVariant(variantIndex) {
    if (this.activeVariant === variantIndex) return
    this.activeVariant = variantIndex
    this.$nextTick(() => this.scheduleCarouselRefresh())
  },

  getActiveGroupElement() {
    return this.$el.querySelector(
      `.lsa-product-card__variant-group[data-variant-index="${this.activeVariant}"]`
    )
  },

  getActiveSwiperElement() {
    const activeGroup = this.getActiveGroupElement()
    if (!activeGroup) return null
    return activeGroup.querySelector(".swiper")
  },

  getSwiperAlpineData(swiperElement) {
    if (!swiperElement) return null
    // Access Alpine component state attached to this .swiper element.
    const alpineStack = swiperElement._x_dataStack || []
    for (const data of alpineStack) {
      if (data && (data.swiperRef || typeof data.initSlider === "function")) {
        return data
      }
    }
    return null
  },

  getSwiperFromGroup(groupElement) {
    if (!groupElement) return null
    const swiperElement = groupElement.querySelector(".swiper")
    if (!swiperElement) return null

    if (swiperElement.swiper) return swiperElement.swiper

    const data = this.getSwiperAlpineData(swiperElement)
    if (data && typeof data.initSlider === "function") {
      data.initSlider()
    }

    return swiperElement.swiper || data?.swiperRef || null
  },

  scheduleCarouselRefresh(attempt = 0) {
    // Wait until Alpine has rendered the active variant's x-if block.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const refreshed = this.refreshActiveCarousel()
        if (!refreshed && attempt < MAX_REFRESH_ATTEMPTS) {
          setTimeout(
            () => this.scheduleCarouselRefresh(attempt + 1),
            REFRESH_RETRY_DELAY_MS
          )
        }
      })
    })
  },

  refreshActiveCarousel() {
    const swiperElement = this.getActiveSwiperElement()
    const activeGroup = swiperElement?.closest(".product-item-slider")
    const swiper = this.getSwiperFromGroup(activeGroup)
    if (!swiper) return false

    swiper.update()
    swiper.updateSlides()
    swiper.updateSize()
    if (swiper.navigation) {
      swiper.navigation.init()
      swiper.navigation.update()
    }
    if (swiper.pagination) {
      swiper.pagination.render()
      swiper.pagination.update()
    }
    swiper.slideTo(0, 0)
    return true
  },

  hoverToNextForGroup(groupElement) {
    const swiper = this.getSwiperFromGroup(groupElement)
    if (swiper && swiper.slides && swiper.slides.length > 1) {
      swiper.slideNext()
    }
  },

  hoverResetForGroup(groupElement, event) {
    const relatedTarget = event?.relatedTarget
    if (relatedTarget && groupElement?.contains(relatedTarget)) return

    const swiper = this.getSwiperFromGroup(groupElement)
    if (swiper) swiper.slideTo(0)
  },
})
